import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { signIn, signInWithGoogle } from "../../redux/actions/authActions";
import SignInLayout from "../../layouts/SignInLayout";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import { useTranslation } from "react-i18next";

import {
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Alert as MuiAlert } from "@material-ui/lab";
import { Link } from "react-router-dom";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const PageShell = styled.div`
  display: flex;
  justify-content: center;
`;

const LoginCard = styled(Paper)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: min(520px, 100%);
  padding: ${(props) => props.theme.spacing(5)}px;
  border-radius: 32px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(18px);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(6)}px;
  }
`;

const BrandMark = styled.div`
  width: 92px;
  height: 92px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #0f172a 0%, #2563eb 60%, #22c55e 100%);
  box-shadow: 0 20px 45px rgba(37, 99, 235, 0.24);
  margin: 0 auto ${(props) => props.theme.spacing(3)}px;

  img {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #2563eb;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Heading = styled(Typography)`
  margin-top: ${(props) => props.theme.spacing(2)}px;
  font-size: clamp(2rem, 3vw, 2.7rem);
  line-height: 1.08;
  letter-spacing: -0.04em;
  font-weight: 800;
  color: #0f172a;
  text-align: center;
`;

const Subtitle = styled(Typography)`
  margin-top: ${(props) => props.theme.spacing(1.5)}px;
  color: #475569;
  font-size: 0.98rem;
  line-height: 1.7;
  text-align: center;
`;

const Form = styled.form`
  margin-top: ${(props) => props.theme.spacing(4)}px;
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: ${(props) => props.theme.spacing(1, 0, 2)}px;

  ${(props) => props.theme.breakpoints.down("xs")} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SubmitButton = styled(Button)`
  min-height: 54px;
  margin-top: ${(props) => props.theme.spacing(1)}px;
  border-radius: 18px;
  text-transform: none;
  font-size: 1rem;
  font-weight: 700;
  background: linear-gradient(135deg, #0f172a 0%, #2563eb 100%);
  box-shadow: 0 18px 35px rgba(37, 99, 235, 0.24);

  &:hover {
    background: linear-gradient(135deg, #0b1220 0%, #1d4ed8 100%);
  }
`;

const PasswordLink = styled(Link)`
  color: #0f766e;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    color: #115e59;
  }
`;

const RememberLabel = styled(FormControlLabel)`
  margin-right: 0;
  .MuiFormControlLabel-label {
    color: #475569;
    font-size: 0.94rem;
  }
`;

function SignIn() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [googleError, setGoogleError] = useState("");

  const resolveGoogleErrorMessage = useCallback(
    (error) => {
      const responseData = error?.response?.data;

      if (typeof responseData === "string" && responseData.trim()) {
        return responseData;
      }

      return (
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        t("auth.googleError")
      );
    },
    [t]
  );

  const handleGoogleCredential = useCallback(
    async (credential) => {
      setGoogleError("");

      try {
        const response = await dispatch(signInWithGoogle(credential));

        if (response?.token) {
          history.push("/dashboard");
          return;
        }

        setGoogleError(response?.message || t("auth.googleError"));
      } catch (error) {
        setGoogleError(resolveGoogleErrorMessage(error));
      }
    },
    [dispatch, history, resolveGoogleErrorMessage, t]
  );

  return (
    <SignInLayout>
      <PageShell>
        <Helmet title={t("auth.signIn")} />
        <LoginCard elevation={0}>
          <BrandMark>
            <img alt="Wifiads" src="/static/img/avatars/logo.png" />
          </BrandMark>
          <Eyebrow>{t("auth.secureAccess")}</Eyebrow>
          <Heading component="h1" variant="h2">
            {t("auth.heroTitle")}
          </Heading>
          <Subtitle variant="body1">{t("auth.heroDescription")}</Subtitle>

          <Formik
            initialValues={{
              email: "",
              password: "",
              submit: false,
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email(t("auth.invalidEmail"))
                .max(255)
                .required(t("auth.emailRequired")),
              password: Yup.string()
                .max(255)
                .required(t("auth.passwordRequired")),
            })}
            onSubmit={async (
              values,
              { setErrors, setStatus, setSubmitting }
            ) => {
              try {
                await dispatch(
                  signIn({
                    email: values.email,
                    password: values.password,
                  })
                );
                history.push("/dashboard");
              } catch (error) {
                const code = error?.response?.data?.code;
                const message = code
                  ? t(`errors.${code}`, t("auth.invalidCredentials"))
                  : t("auth.invalidCredentials");

                setStatus({ success: false });
                setErrors({ submit: message });
                setSubmitting(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                {googleError && (
                  <Alert mt={2} mb={1} severity="warning">
                    {googleError}
                  </Alert>
                )}
                {errors.submit && (
                  <Alert mt={2} mb={1} severity="warning">
                    {errors.submit}
                  </Alert>
                )}
                <TextField
                  type="email"
                  name="email"
                  label={t("auth.email")}
                  value={values.email}
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                  variant="outlined"
                />
                <TextField
                  type="password"
                  name="password"
                  label={t("auth.password")}
                  value={values.password}
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                  variant="outlined"
                />
                <FormFooter>
                  <RememberLabel
                    control={<Checkbox value="remember" color="primary" />}
                    label={t("auth.rememberMe")}
                  />
                  <PasswordLink to="/auth/reset-password">
                    {t("auth.forgotPassword")}
                  </PasswordLink>
                </FormFooter>
                <SubmitButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {t("auth.enterDashboard")}
                </SubmitButton>
                <GoogleSignInButton
                  onCredential={handleGoogleCredential}
                  title={t("auth.continueWithGoogle")}
                />
              </Form>
            )}
          </Formik>
          <Box mt={4} color="#64748b">
            <Typography align="center" variant="body2">
              {t("auth.authorizedOnly")}
            </Typography>
          </Box>
        </LoginCard>
      </PageShell>
    </SignInLayout>
  );
}

export default SignIn;
